import { useEffect, useRef, useState } from "react";
import { Mic, Square, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function AudioRecorder({ onChange }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [url, setUrl] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: rec.mimeType || "audio/webm",
        });
        setUrl(URL.createObjectURL(blob));
        onChange(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      recorderRef.current = rec;
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Micro inaccessible — autorisez le micro ou joignez un fichier.");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    recorderRef.current?.stop();
    setRecording(false);
  };

  const reset = () => {
    if (url) URL.revokeObjectURL(url);
    setUrl(null);
    setSeconds(0);
    onChange(null);
  };

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  if (url) {
    return (
      <div className="rounded-xl border border-[#C5A059]/30 bg-[#F4EFEA] p-3">
        <audio
          data-testid="recorded-audio-preview"
          controls
          src={url}
          className="w-full"
        />
        <button
          type="button"
          data-testid="re-record-button"
          onClick={reset}
          className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-stone-500 transition-colors hover:text-[#C85A32]"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Réenregistrer
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3">
      {recording ? (
        <>
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
          </span>
          <span
            data-testid="recording-timer"
            className="text-sm font-semibold tabular-nums text-stone-800"
          >
            {mm}:{ss}
          </span>
          <span className="flex-1 text-xs text-stone-400">Enregistrement…</span>
          <button
            type="button"
            data-testid="stop-record-button"
            onClick={stop}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600"
            aria-label="Arrêter l'enregistrement"
          >
            <Square className="h-4 w-4" />
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            data-testid="record-button"
            onClick={start}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#C5A059] text-[#1C1917] transition-colors hover:bg-[#D4AF37]"
            aria-label="Enregistrer un message vocal"
          >
            <Mic className="h-5 w-5" />
          </button>
          <span className="text-xs leading-snug text-stone-500">
            Touchez le micro pour enregistrer votre hommage vocal
          </span>
        </>
      )}
    </div>
  );
}
